import { useMemo, useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getEarliestSessionDate,
  getMarketCalendarDate,
  isMarketDayCandidate,
} from '@/lib/sessionDate';

interface SessionDatePickerProps {
  value: string;
  onChange: (date: string) => void;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const monthFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});
const displayDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const dayLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export function SessionDatePicker({ value, onChange }: SessionDatePickerProps) {
  const selectedDate = parseIsoDate(value);
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selectedDate));
  const currentMarketDate = useMemo(() => getMarketCalendarDate(), []);
  const earliestDate = useMemo(() => getEarliestSessionDate(), []);
  const days = getCalendarGrid(visibleMonth);
  const previousDisabled = visibleMonth <= startOfMonth(earliestDate);
  const nextDisabled = visibleMonth >= startOfMonth(currentMarketDate);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
    setOpen(nextOpen);
  };

  const selectDate = (date: Date) => {
    onChange(formatIsoDate(date));
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        aria-label={`Session date ${formatDisplayDate(value)}`}
        className="inline-flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-md border border-line-strong bg-panel px-3 font-sans text-[13px] text-fg hover:bg-panel-2 data-[popup-open]:bg-panel-2 min-[721px]:w-auto min-[721px]:min-w-[172px]"
      >
        <CalendarDays className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} aria-hidden="true" />
        <span>{formatDisplayDate(value)}</span>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="z-50">
          <Popover.Popup
            aria-label="Choose session date"
            className="w-[252px] rounded-md border border-line-strong bg-panel-2 p-2.5 font-mono text-fg shadow-[0_12px_30px_rgba(0,0,0,0.5)] outline-none"
          >
            <div className="mb-2 flex h-7 items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                disabled={previousDisabled}
                onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
                className="grid h-7 w-7 place-items-center rounded-sm text-neutral-500 transition-colors hover:bg-white/[.06] hover:text-fg disabled:cursor-not-allowed disabled:opacity-25"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <span className="text-[11px] font-semibold uppercase text-neutral-200">
                {monthFormatter.format(visibleMonth)}
              </span>
              <button
                type="button"
                aria-label="Next month"
                disabled={nextDisabled}
                onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
                className="grid h-7 w-7 place-items-center rounded-sm text-neutral-500 transition-colors hover:bg-white/[.06] hover:text-fg disabled:cursor-not-allowed disabled:opacity-25"
              >
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>

            <div className="grid grid-cols-7" aria-hidden="true">
              {WEEKDAYS.map((weekday) => (
                <span
                  key={weekday}
                  className="grid h-6 place-items-center text-[9px] uppercase text-neutral-600"
                >
                  {weekday}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-y-0.5" role="grid">
              {days.map((date) => {
                const isoDate = formatIsoDate(date);
                const selected = isoDate === value;
                const outsideMonth = date.getUTCMonth() !== visibleMonth.getUTCMonth();
                const disabled =
                  date < earliestDate || date > currentMarketDate || !isMarketDayCandidate(date);

                return (
                  <button
                    key={isoDate}
                    type="button"
                    role="gridcell"
                    aria-label={dayLabelFormatter.format(date)}
                    aria-selected={selected}
                    aria-current={isoDate === formatIsoDate(currentMarketDate) ? 'date' : undefined}
                    disabled={disabled}
                    onClick={() => selectDate(date)}
                    className={`grid h-7 w-8 place-items-center rounded-sm text-[10px] transition-colors disabled:cursor-not-allowed disabled:text-neutral-700 ${
                      selected
                        ? 'bg-amber-300 font-semibold text-neutral-950'
                        : outsideMonth
                          ? 'text-neutral-700 hover:bg-white/[.05] hover:text-neutral-400'
                          : 'text-neutral-300 hover:bg-white/[.07] hover:text-white'
                    }`}
                  >
                    {date.getUTCDate()}
                  </button>
                );
              })}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

function parseIsoDate(value: string): Date {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? getMarketCalendarDate() : parsed;
}

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

function getCalendarGrid(month: Date): Date[] {
  const firstDay = startOfMonth(month);
  const gridStart = new Date(firstDay);
  gridStart.setUTCDate(firstDay.getUTCDate() - firstDay.getUTCDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setUTCDate(gridStart.getUTCDate() + index);
    return date;
  });
}

function formatIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDisplayDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? date : displayDateFormatter.format(parsed);
}
